import { IonContent, IonHeader, IonPage, IonTitle, IonToolbar, IonButton, IonList, IonItem, IonLabel, IonGrid, IonRow, IonCol, IonModal, IonIcon, IonChip } from '@ionic/react';
import { addCircle, closeCircle } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { getPlotIdsByFilter } from '../services/plot.service';
import { cropTypesState, deviceTypesState, plotTagsState } from '../services/state.service';
import './PlotFilter.css';
import { getAreas, getDistricts, getStates, getSubDistricts } from '../services/location.service';
import SelectItem from './SelectItem';

export interface Filters {
    crop?: boolean;
    device?: boolean;
    tag?: boolean;
    location?: boolean;
}

interface IProps {
    filter: any;
    filterChangeHandler: Function;
    onDoneHandler: Function;
    onCloseHandler: Function;
    hideFilters?: Filters;
}

const PlotFilter: React.FC<IProps> = (props) => {
    const cropTypes = useRecoilValue(cropTypesState);
    const deviceTypes = useRecoilValue(deviceTypesState);
    const plotTags = useRecoilValue(plotTagsState);

    const [selectedCrops, setSelectedCrops] = useState<string[]>(props?.filter?.crops || []);
    const [selectedDevices, setSelectedDevices] = useState<string[]>(props?.filter?.devices || []);
    const [selectedTags, setSelectedTags] = useState<string[]>(props?.filter?.tags || []);
    const [showSelectCropsModal, setShowSelectCropsModal] = useState(false);
    const [showSelectDevicesModal, setShowSelectDevicesModal] = useState(false);
    const [showSelectTagsModal, setShowSelectTagsModal] = useState(false);
    const [showSelectStateModal, setShowSelectStateModal] = useState(false);
    const [showSelectDistrictModal, setShowSelectDistrictModal] = useState(false);
    const [showSelectSubDistrictModal, setShowSelectSubDistrictModal] = useState(false);
    const [showSelectAreaModal, setShowSelectAreaModal] = useState(false);

    const { hideFilters } = props;

    // location dropdown options
    const [states, setStates] = useState<any[]>([]);
    const [districts, setDistricts] = useState<any[]>([]);
    const [subDistricts, setSubdistricts] = useState<any[]>([]);
    const [area, setArea] = useState<any[]>([]);

    // selected locations
    const [selectedState, setSelectedState] = useState<string>(props?.filter?.location?.state || '');
    const [selectedDistrict, setSelectedDistrict] = useState<string>(props?.filter?.location?.district || '');
    const [selectedSubDistrict, setSelectedSubDistrict] = useState<string>(props?.filter?.location?.subDistrict || '');
    const [selectedArea, setSelectedArea] = useState<string>(props?.filter?.location?.area || '');

    // loading states
    const [isStateLoading, setIsStateLoading] = useState<boolean>(true);
    const [isDistrictLoading, setIsDistrictLoading] = useState<boolean>(false);
    const [isSubDistrictLoading, setIsSubDistrictLoading] = useState<boolean>(false);
    const [isAreaLoading, setIsAreaLoading] = useState<boolean>(false);

    useEffect(() => {
        getStates()
            .then((res: { state: string }[]) => {
                setStates(generateOptions(
                    res.map(obj => obj.state).sort()
                ));
                setIsStateLoading(false);
            })
    }, [])

    const compareWith = (o1: any, o2: any) => {
        if (Array.isArray(o2)) {
            return o2.indexOf(o1) !== -1;
        }
        return o1 === o2;
    };

    function getPayloadForLocation(){
        let payload:any = {};

        if(selectedState.length > 0) {
            payload.state = selectedState;
        }
      
        if (selectedDistrict.length > 0) {
            payload.district = selectedDistrict;
        }
    
        if (selectedSubDistrict.length > 0) {
            payload.subDistrict = selectedSubDistrict;
        }
    
        if (selectedArea.length > 0) {
            payload.area = selectedArea;
        }

        return payload;
    }

    async function onDone() {
        try {
          const where: any = {
            and: []
          };
          if (selectedCrops?.length > 0) {
            where.and.push({
              cropId: {
                inq: selectedCrops
              }
            });
          }

          if (selectedDevices?.length > 0) {
            where.and.push({
              deviceType: {
                inq: selectedDevices
              }
            });
          }
    
          if (selectedTags?.length > 0) {
            where.and.push({
              tags: {
                inq: selectedTags
              }
            });
          }
      
          const locationPayload = getPayloadForLocation();  
      
          const locationFilter: any = {};
          if (locationPayload?.state) locationFilter.state = {eq:locationPayload.state};
          if (locationPayload?.district) locationFilter.district = {eq:locationPayload.district};
          if (locationPayload?.subDistrict) locationFilter.taluka = {eq:locationPayload.subDistrict};
          if (locationPayload?.area) locationFilter.place = {eq:locationPayload.area};
      
          if (Object.keys(locationFilter).length > 0) {
            where.and.push(locationFilter);
          }
      
          if (where.and.length === 0) {
            delete where.and;
          }

          // Create filter criteria object to pass to parent
          const filterCriteria = {
            where,
            crops: selectedCrops,
            devices: selectedDevices,
            tags: selectedTags,
            location: locationPayload
          };

          props.filterChangeHandler?.({
            ...props.filter,
            crops: selectedCrops,
            devices: selectedDevices,
            tags: selectedTags,
            location: locationPayload
          });
    
          props.onDoneHandler?.(filterCriteria);
          onClose();
        } catch (err: any) {
          console.error("Failed to apply filters:", err);
        }
      }
      

    function onReset() {
        props.filterChangeHandler({});
        props.onDoneHandler([]);
        onClose();
    }

    function onClose() {
        props.onCloseHandler(false);
    }

    function generateOptions(option: string[]) {
        return option?.map((opt) => {
            return {
                label: opt,
                value: opt
            };
        });
    }

    async function loadDistricts(state: string) {
        setIsDistrictLoading(true);
        getDistricts(state)
            .then((res: { district: string }[]) => {
                unselectOptions(['district', 'subDistrict', 'area']);
                setDistricts(generateOptions(
                    res.map(obj => obj.district).sort()
                ));
                setIsDistrictLoading(false);
            });
    }

    async function loadSubDistricts(district: string) {
        setIsSubDistrictLoading(true);
        getSubDistricts(selectedState, district)
            .then((res: { subDistrict: string }[]) => {
                unselectOptions(['subDistrict', 'area']);
                setSubdistricts(generateOptions(
                    res.map(obj => obj.subDistrict).sort()
                ));
                setIsSubDistrictLoading(false);
            });
    }

    async function loadAreas(subDistrict: string) {
        setIsAreaLoading(true);
        getAreas(selectedState, selectedDistrict, subDistrict)
            .then((res: { area: string }[]) => {
                unselectOptions(['area']);
                setArea(generateOptions(
                    res.map(obj => obj.area).sort()
                ));
                setIsAreaLoading(false);
            });
    }

    async function setState(item: any) {
        setSelectedState(item.value);
        loadDistricts(item.value);
        setShowSelectStateModal(false);
    }

    async function setDistrict(item: any) {
        setSelectedDistrict(item.value);
        loadSubDistricts(item.value);
        setShowSelectDistrictModal(false);
    }

    async function setSubDistrict(item: any) {
        setSelectedSubDistrict(item.value);
        loadAreas(item.value);
        setShowSelectSubDistrictModal(false);
    }

    async function handleAreaSelection(item: any) {
        setSelectedArea(item.value);
        setShowSelectAreaModal(false);
    }

    function handleDropdownChange(dropdown: string, selected: string) {
        const value = selected;
        switch (dropdown) {
            case 'state':
                if (value) setState({ value });
                else {
                    unselectOptions(['state', 'district', 'subDistrict', 'area']);
                    setDistricts([]);
                    setIsDistrictLoading(false);
                }
                break;
            case 'district':
                if (value) setDistrict({ value });
                else {
                    unselectOptions(['district', 'subDistrict', 'area']);
                    setSubdistricts([]);
                    setIsSubDistrictLoading(false);
                }
                break;
            case 'subDistrict':
                if (value) setSubDistrict({ value });
                else {
                    setSelectedSubDistrict('');
                    unselectOptions(['subDistrict', 'area']);
                    setArea([]);
                    setIsAreaLoading(false);
                }
                break;
            case 'area':
                if (value) handleAreaSelection({ value });
                else {
                    unselectOptions(['area']);
                }
                break;
            default:
                return;
        }
    }
    
    /* 
        - function
        - unselects the locations passed in arguments
        - empty the dropdown options of its children, 
            i.e count == 0, it's parent
            count > 0, it's child
    */ 
    function unselectOptions(toUnselect: string[]) { 
        let cnt = 0;
        for (const field of toUnselect) {
            if (field === 'state') {
                setSelectedState('');
                cnt++;
            } else if (field === 'district') {
                setSelectedDistrict('');
                if (cnt++ > 0) setDistricts([]);
            } else if (field === 'subDistrict') {
                setSelectedSubDistrict('');
                if (cnt++ > 0) setSubdistricts([]);
            } else {
                setSelectedArea('');
                if (cnt++ > 0) setArea([]);
            }
        }
    }

    useEffect(() => {
        console.log(selectedCrops);
    }, [selectedCrops]);

    async function addCrop(item: any) {
        setSelectedCrops([...selectedCrops, item.id]);
        setShowSelectCropsModal(false);
    }

    async function removeCrop(cropId: string) {
        setSelectedCrops(selectedCrops.filter(id => id !== cropId));
    }

    async function addDevice(item: any) {
        setSelectedDevices([...selectedDevices, item.deviceName]);
        setShowSelectDevicesModal(false);
    }

    async function removeDevice(deviceType: string) {
        setSelectedDevices(selectedDevices.filter(id => id !== deviceType));
    }

    async function removeTag(tagId: string) {
        setSelectedTags(selectedTags.filter(id => id !== tagId));
    }

    async function addTag(item: any) {
        setSelectedTags([...selectedTags, item.id]);
        setShowSelectTagsModal(false);
    }

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonTitle>Filter</IonTitle>
                </IonToolbar>
            </IonHeader>
            <IonContent fullscreen>
                <IonList>
                    {
                        !hideFilters?.crop && 
                        <>
                            <IonItem lines="none">
                                <IonLabel >Crops</IonLabel>
                                <IonIcon
                                    icon={addCircle}
                                    slot="end"
                                    onClick={(e) => setShowSelectCropsModal(true)}
                                />
                            </IonItem>
                            <IonItem>
                                <IonGrid>
                                    <IonRow onClick={(e) => setShowSelectCropsModal(true)}>
                                        {selectedCrops.length > 0 &&
                                            selectedCrops.map((item, index) => (
                                                <IonCol key={index}>
                                                    <IonChip outline={true} color="primary">
                                                        <IonLabel>{cropTypes.find((crop: { id: string; cropName: string }) => crop.id === item)?.cropName}</IonLabel>
                                                        <IonIcon
                                                            icon={closeCircle}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                removeCrop(item)
                                                            }}
                                                        />
                                                    </IonChip>
                                                </IonCol>
                                            ))}
                                        {selectedCrops.length === 0 && (
                                            <IonCol>
                                                <IonLabel class="secondary-text">
                                                    Select Crop
                                                </IonLabel>
                                            </IonCol>
                                        )}
                                    </IonRow>
                                </IonGrid>
                            </IonItem>
                        </>
                    }

                    {
                        !hideFilters?.device && 
                        <>
                            <IonItem lines="none">
                                <IonLabel >Devices</IonLabel>
                                <IonIcon
                                    icon={addCircle}
                                    slot="end"
                                    onClick={(e) => setShowSelectDevicesModal(true)}
                                />
                            </IonItem>
                            <IonItem>
                                <IonGrid>
                                    <IonRow onClick={(e) => setShowSelectDevicesModal(true)}>
                                        {selectedDevices.length > 0 &&
                                            selectedDevices.map((item, index) => (
                                                <IonCol key={index}>
                                                    <IonChip outline={true} color="primary">
                                                        <IonLabel>{deviceTypes.find((device: { deviceId: string; deviceName: string }) => device.deviceId === item)?.deviceName}</IonLabel>
                                                        <IonIcon
                                                            icon={closeCircle}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                removeDevice(item)
                                                            }}
                                                        />
                                                    </IonChip>
                                                </IonCol>
                                            ))}
                                        {selectedDevices.length === 0 && (
                                            <IonCol>
                                                <IonLabel class="secondary-text">
                                                    Select Device
                                                </IonLabel>
                                            </IonCol>
                                        )}
                                    </IonRow>
                                </IonGrid>
                            </IonItem>
                        </>
                    }

                    {
                        !hideFilters?.tag && 
                        <>
                            <IonItem lines="none">
                                <IonLabel>Tags</IonLabel>
                                <IonIcon
                                    icon={addCircle}
                                    slot="end"
                                    onClick={(e) => setShowSelectTagsModal(true)}
                                />
                            </IonItem>
                            <IonItem>
                                <IonGrid>
                                    <IonRow>
                                        {selectedTags.length > 0 &&
                                            selectedTags.map((item, index) => (
                                                <IonCol key={index}>
                                                    <IonChip outline={true} color="primary">
                                                        <IonLabel>{item}</IonLabel>
                                                        <IonIcon
                                                            icon={closeCircle}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                removeTag(item)
                                                            }}
                                                        />
                                                    </IonChip>
                                                </IonCol>
                                            ))}
                                        {selectedTags.length === 0 && (
                                            <IonCol>
                                                <IonLabel class="secondary-text">
                                                    Select Tag
                                                </IonLabel>
                                            </IonCol>
                                        )}
                                    </IonRow>
                                </IonGrid>
                            </IonItem>
                        </>
                    }

                    { !hideFilters?.location &&
                        <>
                            <IonItem lines="none">
                                <IonLabel>State</IonLabel>
                            </IonItem>
                            <IonItem>
                                <IonGrid>
                                    <IonRow onClick={() => setShowSelectStateModal(true)}>
                                        {selectedState ? (
                                            <IonCol>
                                                <IonChip outline={true} color="primary">
                                                    <IonLabel>{selectedState}</IonLabel>
                                                    <IonIcon
                                                        icon={closeCircle}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDropdownChange('state', '');
                                                        }}
                                                    />
                                                </IonChip>
                                            </IonCol>
                                        ) : (
                                            <IonCol>
                                                <IonLabel class="secondary-text">
                                                    Select State
                                                </IonLabel>
                                            </IonCol>
                                        )}
                                    </IonRow>
                                </IonGrid>
                            </IonItem>

                            <IonItem lines="none">
                                <IonLabel>District</IonLabel>
                            </IonItem>
                            <IonItem className={!selectedState ? 'disabled' : ''}>
                                <IonGrid>
                                    <IonRow onClick={() => selectedState && !districts.length ? loadDistricts(selectedState) : setShowSelectDistrictModal(true)}>
                                        {selectedDistrict ? (
                                            <IonCol>
                                                <IonChip outline={true} color="primary">
                                                    <IonLabel>{selectedDistrict}</IonLabel>
                                                    <IonIcon
                                                        icon={closeCircle}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDropdownChange('district', '');
                                                        }}
                                                    />
                                                </IonChip>
                                            </IonCol>
                                        ) : (
                                            <IonCol>
                                                <IonLabel class="secondary-text">
                                                    {!selectedState ? 'Select State First' : isDistrictLoading ? 'Loading...' : 'Select District'}
                                                </IonLabel>
                                            </IonCol>
                                        )}
                                    </IonRow>
                                </IonGrid>
                            </IonItem>

                            <IonItem lines="none">
                                <IonLabel>Sub District</IonLabel>
                            </IonItem>
                            <IonItem className={!selectedDistrict ? 'disabled' : ''}>
                                <IonGrid>
                                    <IonRow onClick={() => selectedDistrict && !subDistricts.length ? loadSubDistricts(selectedDistrict) : setShowSelectSubDistrictModal(true)}>
                                        {selectedSubDistrict ? (
                                            <IonCol>
                                                <IonChip outline={true} color="primary">
                                                    <IonLabel>{selectedSubDistrict}</IonLabel>
                                                    <IonIcon
                                                        icon={closeCircle}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDropdownChange('subDistrict', '');
                                                        }}
                                                    />
                                                </IonChip>
                                            </IonCol>
                                        ) : (
                                            <IonCol>
                                                <IonLabel class="secondary-text">
                                                    {!selectedDistrict ? 'Select District First' : isSubDistrictLoading ? 'Loading...' : 'Select Sub District'}
                                                </IonLabel>
                                            </IonCol>
                                        )}
                                    </IonRow>
                                </IonGrid>
                            </IonItem>

                            <IonItem lines="none">
                                <IonLabel>Area</IonLabel>
                            </IonItem>
                            <IonItem className={!selectedSubDistrict ? 'disabled' : ''}>
                                <IonGrid>
                                    <IonRow onClick={() => selectedSubDistrict && !area.length ? loadAreas(selectedSubDistrict) : setShowSelectAreaModal(true)}>
                                        {selectedArea ? (
                                            <IonCol>
                                                <IonChip outline={true} color="primary">
                                                    <IonLabel>{selectedArea}</IonLabel>
                                                    <IonIcon
                                                        icon={closeCircle}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDropdownChange('area', '');
                                                        }}
                                                    />
                                                </IonChip>
                                            </IonCol>
                                        ) : (
                                            <IonCol>
                                                <IonLabel class="secondary-text">
                                                    {!selectedSubDistrict ? 'Select Sub District First' : isAreaLoading ? 'Loading...' : 'Select Area'}
                                                </IonLabel>
                                            </IonCol>
                                        )}
                                    </IonRow>
                                </IonGrid>
                            </IonItem>
                        </>
                    }

                </IonList>
                <IonGrid>
                    <IonRow>
                        <IonCol><IonButton color="danger" className="shiftRight" onClick={onReset}>Reset</IonButton></IonCol>
                        <IonCol><IonButton onClick={onDone}>Done</IonButton></IonCol>
                    </IonRow>
                </IonGrid>

                {showSelectCropsModal && (
                    <IonModal isOpen={showSelectCropsModal}>
                        <SelectItem
                            label="Select Crop"
                            items={cropTypes.filter(
                                (item: any) =>
                                    !selectedCrops.includes(item.id)
                            )}
                            labelKey="cropName"
                            valueKey="id"
                            setItem={addCrop}
                            selectedValue=""
                            onClose={setShowSelectCropsModal}
                        />
                    </IonModal>
                )}

                {showSelectDevicesModal && (
                    <IonModal isOpen={showSelectDevicesModal}>
                        <SelectItem
                            label="Select Device"
                            items={deviceTypes.filter(
                                (item: any) =>
                                    !selectedDevices.includes(item)
                            )}
                            labelKey="deviceName"
                            valueKey="deviceId"
                            setItem={addDevice}
                            selectedValue=""
                            onClose={setShowSelectDevicesModal}
                        />
                    </IonModal>
                )}

                {showSelectTagsModal && (
                    <IonModal isOpen={showSelectTagsModal}>
                        <SelectItem
                            label="Select Tag"
                            items={plotTags.filter(
                                (item: any) =>
                                    !selectedTags.includes(item.id)
                            )}
                            labelKey="id"
                            valueKey="id"
                            setItem={addTag}
                            selectedValue=""
                            onClose={setShowSelectTagsModal}
                        />
                    </IonModal>
                )}

                {showSelectStateModal && (
                    <IonModal isOpen={showSelectStateModal}>
                        <SelectItem
                            label="Select State"
                            items={states}
                            labelKey="label"
                            valueKey="value"
                            setItem={setState}
                            selectedValue={selectedState}
                            onClose={setShowSelectStateModal}
                        />
                    </IonModal>
                )}

                {showSelectDistrictModal && (
                    <IonModal isOpen={showSelectDistrictModal}>
                        <SelectItem
                            label="Select District"
                            items={districts}
                            labelKey="label"
                            valueKey="value"
                            setItem={setDistrict}
                            selectedValue={selectedDistrict}
                            onClose={setShowSelectDistrictModal}
                        />
                    </IonModal>
                )}

                {showSelectSubDistrictModal && (
                    <IonModal isOpen={showSelectSubDistrictModal}>
                        <SelectItem
                            label="Select Sub District"
                            items={subDistricts}
                            labelKey="label"
                            valueKey="value"
                            setItem={setSubDistrict}
                            selectedValue={selectedSubDistrict}
                            onClose={setShowSelectSubDistrictModal}
                        />
                    </IonModal>
                )}

                {showSelectAreaModal && (
                    <IonModal isOpen={showSelectAreaModal}>
                        <SelectItem
                            label="Select Area"
                            items={area}
                            labelKey="label"
                            valueKey="value"
                            setItem={handleAreaSelection}
                            selectedValue={selectedArea}
                            onClose={setShowSelectAreaModal}
                        />
                    </IonModal>
                )}
            </IonContent>
        </IonPage>
    );
};

export default PlotFilter;
